import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  MatSort,
  MatSortModule,
  Sort,
  SortDirection,
} from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {
  ApplicationLog,
  ApplicationLogListRequest,
  LogService,
} from '../../../services/log.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { LogDetailDialogComponent } from './log-detail-dialog/log-detail-dialog.component';

@Component({
  selector: 'app-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ErrorStateComponent,
  ],
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.scss'],
})
export class LogsComponent implements OnInit, OnDestroy {
  private logService = inject(LogService);
  private snackbarService = inject(SnackbarService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);

  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);

  private destroy$ = new Subject<void>();

  // Data
  logs: ApplicationLog[] = [];
  dataSource = new MatTableDataSource<ApplicationLog>([]);
  displayedColumns: string[] = [
    'timestamp',
    'level',
    'user',
    'message',
    'requestPath',
  ];
  logLevels: string[] = ['Information', 'Warning', 'Error', 'Fatal'];

  // State
  isLoading = false;
  error: string | null = null;

  // Pagination
  totalCount = 0;
  pageSize = 25;
  pageIndex = 0;
  pageSizeOptions = [10, 25, 50, 100];

  // Sorting
  sortBy = 'timestamp';
  sortDirection: SortDirection = 'desc';

  // Search/Filter
  filterForm!: FormGroup;
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.initializeFilterForm();
    this.loadLogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFilterForm(): void {
    // Generate default date values for today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const startOfDay = `${year}-${month}-${day}T00:00`;
    const endOfDay = `${year}-${month}-${day}T23:59`;

    // Default to Error and Fatal levels on load
    this.filterForm = this.fb.group({
      levels: [['Error', 'Fatal']],
      startDate: [startOfDay],
      endDate: [endOfDay],
    });

    // Subscribe to levels filter changes - debounceTime(0) ensures immediate but coalesced updates
    this.filterForm
      .get('levels')
      ?.valueChanges.pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadLogs();
      });
  }

  loadLogs(): void {
    this.isLoading = true;
    this.error = null;

    const formValue = this.filterForm.value;
    const levels = formValue.levels || [];

    // Format dates if provided
    const startDate = formValue.startDate
      ? this.formatDateForApi(formValue.startDate, true)
      : undefined;
    const endDate = formValue.endDate
      ? this.formatDateForApi(formValue.endDate, false)
      : undefined;

    const request: ApplicationLogListRequest = {
      page: this.pageIndex + 1, // API uses 1-based pagination
      pageSize: this.pageSize,
      search: this.searchQuery?.trim() || undefined,
      levels: levels.length > 0 ? levels : undefined,
      startDate,
      endDate,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
    };

    this.logService
      .getApplicationLogs(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.logs = result.items;
          this.dataSource.data = result.items;
          this.totalCount = result.totalCount;
          this.isLoading = false;
        },
        error: () => {
          this.error = 'Failed to load system logs. Please try again.';
          this.isLoading = false;
          this.snackbarService.showError('Failed to load system logs');
        },
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadLogs();
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active || 'timestamp';
    this.sortDirection = sort.direction || 'desc';
    this.pageIndex = 0;
    this.loadLogs();
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.pageIndex = 0;
      this.loadLogs();
    }, 400);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.pageIndex = 0;
    this.loadLogs();
  }

  resetFilters(): void {
    this.searchQuery = '';

    // Reset to default values (Error/Fatal levels, today's date range)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const startOfDay = `${year}-${month}-${day}T00:00`;
    const endOfDay = `${year}-${month}-${day}T23:59`;

    this.filterForm.reset({
      levels: ['Error', 'Fatal'],
      startDate: startOfDay,
      endDate: endOfDay,
    });

    // Reset sorting to default (timestamp descending)
    this.sortBy = 'timestamp';
    this.sortDirection = 'desc';
    const sort = this.sort();
    sort.active = 'timestamp';
    sort.direction = 'desc';
    sort.sortChange.emit({ active: 'timestamp', direction: 'desc' });

    this.pageIndex = 0;
    this.loadLogs();
  }

  hasActiveFilters(): boolean {
    const formValue = this.filterForm.value;
    return !!(
      this.searchQuery?.trim() ||
      (formValue.levels && formValue.levels.length > 0) ||
      formValue.startDate ||
      formValue.endDate
    );
  }

  hasFiltersChanged(): boolean {
    // Check if any filters differ from their default values
    const formValue = this.filterForm.value;

    // Get today's default date range
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const defaultStartDate = `${year}-${month}-${day}T00:00`;
    const defaultEndDate = `${year}-${month}-${day}T23:59`;

    // Default levels are ['Error', 'Fatal']
    const defaultLevels = ['Error', 'Fatal'];
    const currentLevels = formValue.levels || [];
    const levelsChanged =
      currentLevels.length !== defaultLevels.length ||
      !defaultLevels.every((level: string) => currentLevels.includes(level));

    // Check if sorting differs from default (timestamp desc)
    const sortingChanged =
      this.sortBy !== 'timestamp' || this.sortDirection !== 'desc';

    return !!(
      this.searchQuery?.trim() ||
      levelsChanged ||
      formValue.startDate !== defaultStartDate ||
      formValue.endDate !== defaultEndDate ||
      sortingChanged
    );
  }

  onDateChange(): void {
    this.pageIndex = 0;
    this.loadLogs();
  }

  private formatDateForApi(date: string | Date, isStartDate: boolean): string {
    // datetime-local returns a string in format "YYYY-MM-DDTHH:mm" in local time
    // We need to convert to UTC since timestamps are stored in UTC in the database
    let localDate: Date;

    if (typeof date === 'string') {
      // Parse the datetime-local string as local time
      localDate = new Date(date);
    } else {
      localDate = new Date(date);
    }

    // Convert to UTC ISO string and return without the 'Z' suffix
    // toISOString() returns UTC time in format "YYYY-MM-DDTHH:mm:ss.sssZ"
    return localDate.toISOString().slice(0, 19);
  }

  getLevelClass(level: string): string {
    switch (level?.toLowerCase()) {
      case 'error':
      case 'fatal':
        return 'level-error';
      case 'warning':
        return 'level-warning';
      case 'information':
        return 'level-info';
      default:
        return 'level-debug';
    }
  }

  formatTimestamp(timestamp: string): string {
    // Backend sends DateTimeOffset with timezone information
    return new Date(timestamp).toLocaleString();
  }

  truncateMessage(message: string, maxLength: number = 150): string {
    if (!message) return '';
    return message.length > maxLength
      ? message.substring(0, maxLength) + '...'
      : message;
  }

  openLogDetail(log: ApplicationLog): void {
    this.dialog.open(LogDetailDialogComponent, {
      data: { log },
      width: '700px',
      maxHeight: '90vh',
    });
  }
}
